import { useState } from "react";
import { Box, Text, useThemeUI } from "theme-ui";

import { useTranslation } from "hooks";
import { Select, ToggleText } from "components";
import flexMap from "./flexMap";
import getRecommendedFlex from "./getRecommendedFlex";
import * as styles from "./FlexGuide.styles";

const WEIGHTS = [
  { value: "40", label: "< 40 lbs.", labelMetric: "< 18.14 kg" },
  { value: "45", label: "< 45 lbs.", labelMetric: "< 20.41 kg" },
  { value: "50", label: "< 50 lbs.", labelMetric: "< 22.68 kg" },
  { value: "55", label: "< 55 lbs.", labelMetric: "< 24.95 kg" },
  { value: "60", label: "< 60 lbs.", labelMetric: "< 27.22 kg" },
  { value: "65", label: "< 65 lbs.", labelMetric: "< 29.48 kg" },
  { value: "70", label: "< 70 lbs.", labelMetric: "< 31.75 kg" },
  { value: "75", label: "< 75 lbs.", labelMetric: "< 34.02 kg" },
  { value: "80", label: "< 80 lbs.", labelMetric: "< 36.29 kg" },
  { value: "85", label: "< 85 lbs.", labelMetric: "< 38.56 kg" },
  { value: "90", label: "< 90 lbs.", labelMetric: "< 40.82 kg" },
  { value: "95", label: "< 95 lbs.", labelMetric: "< 43.09 kg" },
  { value: "100", label: "100 lbs.", labelMetric: "45.36 kg" },
  { value: "105", label: "105 lbs.", labelMetric: "47.63 kg" },
  { value: "110", label: "110 lbs.", labelMetric: "49.90 kg" },
  { value: "115", label: "115 lbs.", labelMetric: "52.16 kg" },
  { value: "120", label: "120 lbs.", labelMetric: "54.43 kg" },
  { value: "125", label: "125 lbs.", labelMetric: "56.70 kg" },
  { value: "130", label: "130 lbs.", labelMetric: "58.97 kg" },
  { value: "135", label: "135 lbs.", labelMetric: "61.24 kg" },
  { value: "140", label: "140 lbs.", labelMetric: "63.50 kg" },
  { value: "145", label: "145 lbs.", labelMetric: "65.77 kg" },
  { value: "150", label: "150 lbs.", labelMetric: "68.04 kg" },
  { value: "155", label: "155 lbs.", labelMetric: "70.31 kg" },
  { value: "160", label: "160 lbs.", labelMetric: "72.58 kg" },
  { value: "165", label: "165 lbs.", labelMetric: "74.84 kg" },
  { value: "170", label: "170 lbs.", labelMetric: "77.11 kg" },
  { value: "175", label: "175 lbs.", labelMetric: "79.38 kg" },
  { value: "180", label: "180 lbs.", labelMetric: "81.65 kg" },
  { value: "185", label: "185 lbs.", labelMetric: "83.92 kg" },
  { value: "190", label: "190 lbs.", labelMetric: "86.18 kg" },
  { value: "195", label: "195 lbs.", labelMetric: "88.45 kg" },
  { value: "200", label: "200 lbs.", labelMetric: "90.72 kg" },
  { value: "205", label: "205 lbs.", labelMetric: "92.99 kg" },
  { value: "210", label: "210 lbs.", labelMetric: "95.26 kg" },
  { value: "215", label: "215 lbs.", labelMetric: "97.52 kg" },
  { value: "220", label: "220 lbs.", labelMetric: "99.79 kg" },
  { value: "225", label: "225 lbs.", labelMetric: "102.06 kg" },
  { value: "230", label: "230 lbs.", labelMetric: "104.33 kg" },
  { value: "235", label: "235 lbs.", labelMetric: "106.60 kg" },
  { value: "240", label: "240 lbs.", labelMetric: "108.86 kg" },
  { value: "245", label: "245 lbs.", labelMetric: "111.13 kg" },
  { value: "250", label: "250 lbs.", labelMetric: "113.40 kg" },
  { value: "255", label: "255 lbs.", labelMetric: "115.67 kg" },
  { value: "260", label: "260 lbs.", labelMetric: "117.94 kg" },
  { value: "265", label: "265 lbs.", labelMetric: "120.20 kg" },
  { value: "270", label: "270 lbs.", labelMetric: "122.47 kg" },
  { value: "275", label: "275 lbs.", labelMetric: "124.74 kg" },
  { value: "280", label: "280 lbs.", labelMetric: "127.01 kg" },
  { value: "285", label: "285 lbs.", labelMetric: "129.28 kg" },
  { value: "290", label: "290 lbs.", labelMetric: "131.54 kg" },
  { value: "295", label: "295 lbs.", labelMetric: "133.81 kg" },
  { value: "300", label: "300 lbs.", labelMetric: "136.08 kg" },
  { value: "301", label: "> 300 lbs.", labelMetric: "> 136.08 kg" },
];

const HEIGHTS = [
  { value: "101.6", label: "< 3'4\"", labelMetric: "< 101.6 cm" },
  { value: "104.14", label: "< 3'5\"", labelMetric: "< 104.14 cm" },
  { value: "106.68", label: "< 3'6\"", labelMetric: "< 106.68 cm" },
  { value: "109.22", label: "< 3'7\"", labelMetric: "< 109.22 cm" },
  { value: "111.76", label: "< 3'8\"", labelMetric: "< 111.76 cm" },
  { value: "114.3", label: "< 3'9\"", labelMetric: "< 114.3 cm" },
  { value: "116.84", label: "< 3'10\"", labelMetric: "< 116.84 cm" },
  { value: "119.38", label: "< 3'11\"", labelMetric: "< 119.38 cm" },
  { value: "121.92", label: "< 4'", labelMetric: "< 121.92 cm" },
  { value: "124.46", label: "4'1\"", labelMetric: "124.46 cm" },
  { value: "127", label: "4'2\"", labelMetric: "127 cm" },
  { value: "129.54", label: "4'3\"", labelMetric: "129.54 cm" },
  { value: "132.08", label: "4'4\"", labelMetric: "132.08 cm" },
  { value: "134.62", label: "4'5\"", labelMetric: "134.62 cm" },
  { value: "137.16", label: "4'6\"", labelMetric: "137.16 cm" },
  { value: "139.7", label: "4'7\"", labelMetric: "139.7 cm" },
  { value: "142.24", label: "4'8\"", labelMetric: "142.24 cm" },
  { value: "144.78", label: "4'9\"", labelMetric: "144.78 cm" },
  { value: "147.32", label: "4'10\"", labelMetric: "147.32 cm" },
  { value: "149.86", label: "4'11\"", labelMetric: "149.86 cm" },
  { value: "152.4", label: "5'", labelMetric: "152.4 cm" },
  { value: "154.94", label: "5'1\"", labelMetric: "154.94 cm" },
  { value: "157.48", label: "5'2\"", labelMetric: "157.48 cm" },
  { value: "160.02", label: "5'3\"", labelMetric: "160.02 cm" },
  { value: "162.56", label: "5'4\"", labelMetric: "162.56 cm" },
  { value: "165.1", label: "5'5\"", labelMetric: "165.1 cm" },
  { value: "167.64", label: "5'6\"", labelMetric: "167.64 cm" },
  { value: "170.18", label: "5'7\"", labelMetric: "170.18 cm" },
  { value: "172.72", label: "5'8\"", labelMetric: "172.72 cm" },
  { value: "175.26", label: "5'9\"", labelMetric: "175.26 cm" },
  { value: "177.8", label: "5'10\"", labelMetric: "177.8 cm" },
  { value: "180.34", label: "5'11\"", labelMetric: "180.34 cm" },
  { value: "182.88", label: "6'", labelMetric: "182.88 cm" },
  { value: "185.42", label: "6'1\"", labelMetric: "185.42 cm" },
  { value: "187.96", label: "6'2\"", labelMetric: "187.96 cm" },
  { value: "190.5", label: "6'3\"", labelMetric: "190.5 cm" },
  { value: "193.04", label: "6'4\"", labelMetric: "193.04 cm" },
  { value: "195.58", label: "6'5\"", labelMetric: "195.58 cm" },
  { value: "198.12", label: "6'6\"", labelMetric: "198.12 cm" },
  { value: "200.66", label: "6'7\"", labelMetric: "200.66 cm" },
  { value: "203.2", label: "6'8\"", labelMetric: "203.2 cm" },
  { value: "204", label: "> 6'8\"", labelMetric: "> 204 cm" },
];

export default function FlexGuide({ showUnitsToggle = false }) {
  const {
    theme: { isMobile },
  } = useThemeUI();

  const { t } = useTranslation();

  const [approxWeight, setApproxWeight] = useState("");
  const [approxHeight, setApproxHeight] = useState("");
  const [unit, setUnit] = useState("Imperial");

  const recommendedFlex =
    approxWeight && approxHeight
      ? getRecommendedFlex({
          weight: approxWeight,
          height: approxHeight,
          flexMap,
        })
      : "--";

  const inputSize = isMobile ? "medium" : "large";

  const units = ["Imperial", "Metric"];

  return (
    <Box>
      <Box sx={styles.selects}>
        <Box sx={styles.select}>
          <Select
            variant="round"
            size={inputSize}
            value={approxWeight}
            onChange={({ target }) => setApproxWeight(target.value)}
          >
            <option key="__blank" value="" disabled={approxWeight}>
              {t("Approx. Weight")}
            </option>
            {WEIGHTS.map(({ value, label, labelMetric }) => (
              <option key={value} value={value}>
                {unit === "Metric" ? labelMetric : label}
              </option>
            ))}
          </Select>
        </Box>
        <Box sx={styles.select}>
          <Select
            variant="round"
            size={inputSize}
            value={approxHeight}
            onChange={({ target }) => setApproxHeight(target.value)}
          >
            <option key="__blank" value="" disabled={approxHeight}>
              {t("Approx. Height")}
            </option>
            {HEIGHTS.map(({ value, label, labelMetric }) => (
              <option key={value} value={value}>
                {unit === "Metric" ? labelMetric : label}
              </option>
            ))}
          </Select>
        </Box>
      </Box>
      {showUnitsToggle && (
        <Box sx={{ paddingBottom: "40px", paddingInline: "40px" }}>
          <ToggleText
            value={unit}
            options={units.map((unit) => ({
              value: unit,
              label: unit,
            }))}
            onChange={setUnit}
            isRound
          />
        </Box>
      )}

      <Box sx={styles.recommendation}>
        <Text variant="h5Light">{t("Your Recommended Flex is")}</Text>
        <Text variant="h1">{recommendedFlex}</Text>
      </Box>
    </Box>
  );
}
